import { motion, AnimatePresence } from 'framer-motion'
import { Wifi, WifiOff, Shield, RefreshCw, ArrowLeft, LogIn } from 'lucide-react'
import { cn } from '@/lib/utils'
import { modalBackdrop, modalContent, tapScale } from '@/lib/motion'

export type ConnectionStatus = 'reconnecting' | 'disconnected' | 'session-expired'

export interface ConnectionOverlayProps {
  status: ConnectionStatus
  visible: boolean
  onRetry?: () => void
  onReturnToLobby?: () => void
  onReLogin?: () => void
}

function AnimatedDots() {
  return (
    <span className="inline-flex gap-0.5 ml-1">
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="w-1.5 h-1.5 rounded-full bg-gold"
          animate={{ opacity: [0.2, 1, 0.2] }}
          transition={{
            duration: 1.2,
            repeat: Infinity,
            delay: i * 0.2,
          }}
        />
      ))}
    </span>
  )
}

function ReconnectingContent() {
  return (
    <>
      <motion.div
        animate={{ scale: [1, 1.1, 1] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        className="w-16 h-16 rounded-2xl bg-gold/10 border border-gold/20 flex items-center justify-center mb-5"
      >
        <Wifi className="w-8 h-8 text-gold" />
      </motion.div>
      <h3 className="text-xl font-display font-semibold text-foreground mb-1">
        重新連接中
      </h3>
      <p className="text-sm text-muted-foreground mb-1">Reconnecting</p>
      <div className="flex items-center justify-center mt-3">
        <span className="text-xs text-muted-foreground">正在嘗試重新連線</span>
        <AnimatedDots />
      </div>
    </>
  )
}

function DisconnectedContent({
  onRetry,
  onReturnToLobby,
}: {
  onRetry?: () => void
  onReturnToLobby?: () => void
}) {
  return (
    <>
      <div className="w-16 h-16 rounded-2xl bg-destructive/10 border border-destructive/20 flex items-center justify-center mb-5">
        <WifiOff className="w-8 h-8 text-destructive" />
      </div>
      <h3 className="text-xl font-display font-semibold text-foreground mb-1">
        連線中斷
      </h3>
      <p className="text-sm text-muted-foreground mb-4">Connection Lost</p>
      <p className="text-xs text-muted-foreground max-w-[240px] text-center mb-6">
        您的網路連線已中斷，請檢查網路狀態後重試
      </p>
      <div className="flex flex-col gap-3 w-full">
        <motion.button
          whileTap={tapScale}
          onClick={onRetry}
          className="w-full h-12 rounded-xl btn-primary font-bold text-sm flex items-center justify-center gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          重新連線
        </motion.button>
        <motion.button
          whileTap={tapScale}
          onClick={onReturnToLobby}
          className="w-full h-12 rounded-xl glass-card font-semibold text-sm text-muted-foreground hover:text-foreground flex items-center justify-center gap-2 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          返回大廳
        </motion.button>
      </div>
    </>
  )
}

function SessionExpiredContent({ onReLogin }: { onReLogin?: () => void }) {
  return (
    <>
      <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mb-5">
        <Shield className="w-8 h-8 text-amber-500" />
      </div>
      <h3 className="text-xl font-display font-semibold text-foreground mb-1">
        登入逾期
      </h3>
      <p className="text-sm text-muted-foreground mb-4">Session Expired</p>
      <p className="text-xs text-muted-foreground max-w-[240px] text-center mb-6">
        您的登入已過期，請重新登入以繼續使用
      </p>
      <motion.button
        whileTap={tapScale}
        onClick={onReLogin}
        className="w-full h-12 rounded-xl btn-primary font-bold text-sm flex items-center justify-center gap-2"
      >
        <LogIn className="w-4 h-4" />
        重新登入
      </motion.button>
    </>
  )
}

export function ConnectionOverlay({
  status,
  visible,
  onRetry,
  onReturnToLobby,
  onReLogin,
}: ConnectionOverlayProps) {
  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          key="connection-overlay"
          variants={modalBackdrop}
          initial="hidden"
          animate="visible"
          exit="exit"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm"
        >
          <motion.div
            variants={modalContent}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={cn(
              'relative mx-6 w-full max-w-sm p-8 rounded-3xl text-center',
              'glass-card-elevated border-border/50'
            )}
          >
            <div className="flex flex-col items-center">
              {status === 'reconnecting' && <ReconnectingContent />}
              {status === 'disconnected' && (
                <DisconnectedContent
                  onRetry={onRetry}
                  onReturnToLobby={onReturnToLobby}
                />
              )}
              {status === 'session-expired' && (
                <SessionExpiredContent onReLogin={onReLogin} />
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
